/**
 * Prepare manuscript map leaves for book export (#177).
 * Rasterizes via mapToSvg at a caller-chosen DPI (default 300).
 */
import { parseMapDocument } from "../lib/mapDocument";
import { mapPage } from "../lib/mapPage";
import { mapToSvg } from "../lib/mapToSvg";
import { rasterizeSvgToPngBytes } from "../lib/mapRasterize";
import { isJust, just, nothing, type Maybe } from "../lib/maybe";
import { joinProjectPath } from "../lib/runtime";
import {
  listProjectAssets,
  readProjectAssetDataUrl,
} from "../lib/tauri/assets";
import { readProjectFile } from "../lib/tauri/core";
import type { ZwMapFigure, ZwPrintSize } from "../lib/zwConfig";
import {
  cssLengthToPx,
  printPageSizeFor,
  type PrintPageSize,
} from "./printSizes";

/** Body text margin for print (0.75in), matching native PDF. */
export const EXPORT_TEXT_MARGIN_IN = 0.75;
/** Printer safety margin (~0.5in from trim). */
export const EXPORT_SAFE_MARGIN_IN = 0.5;
export const DEFAULT_MAP_EXPORT_DPI = 300;

export type MapExportImage = {
  readonly bytes: Uint8Array;
  readonly dataUrl: string;
  readonly widthPx: number;
  readonly heightPx: number;
};

export type PreparedMapFigure = {
  readonly id: string;
  readonly title: string;
  readonly spread: ZwMapFigure["spread"];
  readonly printExtent: ZwMapFigure["printExtent"];
  readonly objectFit: ZwMapFigure["objectFit"];
  /**
   * Single: rotated map at intrinsic aspect (PDF uses CSS object-fit against the
   * real page box; DOCX fits at insert time). Spread: cover/contain-fitted
   * double-width image (EPUB uses this; halves are the print split).
   */
  readonly full: MapExportImage;
  /** Verso/recto halves when spread; nothing for single. */
  readonly halves: Maybe<{
    readonly verso: MapExportImage;
    readonly recto: MapExportImage;
  }>;
};

export type PrepareMapFigureOptions = {
  readonly rootPath: string;
  readonly printSize: ZwPrintSize;
  readonly dpi?: number;
};

function inchesFromCss(value: string): number {
  return cssLengthToPx(value) / 96;
}

/** Content box inside one page for the given print extent, in inches. */
export function mapContentSizeInches(
  page: PrintPageSize,
  printExtent: ZwMapFigure["printExtent"],
): { width: number; height: number; marginIn: number } {
  const pageW = inchesFromCss(page.width);
  const pageH = inchesFromCss(page.height);
  if (printExtent === "fullBleed") {
    return { width: pageW, height: pageH, marginIn: 0 };
  }
  const marginIn =
    printExtent === "textMargins"
      ? EXPORT_TEXT_MARGIN_IN
      : EXPORT_SAFE_MARGIN_IN;
  return {
    width: Math.max(0.5, pageW - marginIn * 2),
    height: Math.max(0.5, pageH - marginIn * 2),
    marginIn,
  };
}

export function pngBytesToDataUrl(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return `data:image/png;base64,${btoa(binary)}`;
}

async function loadImageFromBytes(
  bytes: Uint8Array,
): Promise<HTMLImageElement> {
  const blob = new Blob([bytes as BlobPart], { type: "image/png" });
  const url = URL.createObjectURL(blob);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to decode map PNG"));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function canvasToPngBytes(
  canvas: HTMLCanvasElement,
): Promise<Uint8Array> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value === null) reject(new Error("PNG encode failed"));
      else resolve(value);
    }, "image/png");
  });
  return new Uint8Array(await blob.arrayBuffer());
}

/** Rotate a PNG by 0/90/180/270 clockwise. */
export async function rotatePngBytes(
  bytes: Uint8Array,
  degrees: ZwMapFigure["orientation"],
): Promise<MapExportImage> {
  if (degrees === 0) {
    const img = await loadImageFromBytes(bytes);
    const widthPx = img.naturalWidth;
    const heightPx = img.naturalHeight;
    return {
      bytes,
      dataUrl: pngBytesToDataUrl(bytes),
      widthPx,
      heightPx,
    };
  }
  const img = await loadImageFromBytes(bytes);
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const swap = degrees === 90 || degrees === 270;
  const widthPx = swap ? srcH : srcW;
  const heightPx = swap ? srcW : srcH;
  const canvas = document.createElement("canvas");
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("Could not create canvas for map rotate");
  ctx.translate(widthPx / 2, heightPx / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -srcW / 2, -srcH / 2);
  const out = await canvasToPngBytes(canvas);
  return {
    bytes: out,
    dataUrl: pngBytesToDataUrl(out),
    widthPx,
    heightPx,
  };
}

/** Split a landscape PNG into equal left (verso) and right (recto) halves. */
export async function splitPngHorizontal(
  image: MapExportImage,
): Promise<{ verso: MapExportImage; recto: MapExportImage }> {
  const halfWidth = Math.max(1, Math.floor(image.widthPx / 2));
  const heightPx = image.heightPx;
  const img = await loadImageFromBytes(image.bytes);

  async function crop(sx: number): Promise<MapExportImage> {
    const canvas = document.createElement("canvas");
    canvas.width = halfWidth;
    canvas.height = heightPx;
    const ctx = canvas.getContext("2d");
    if (ctx === null) throw new Error("Could not create canvas for map split");
    ctx.drawImage(img, sx, 0, halfWidth, heightPx, 0, 0, halfWidth, heightPx);
    const bytes = await canvasToPngBytes(canvas);
    return {
      bytes,
      dataUrl: pngBytesToDataUrl(bytes),
      widthPx: halfWidth,
      heightPx,
    };
  }

  const verso = await crop(0);
  const recto = await crop(image.widthPx - halfWidth);
  return { verso, recto };
}

/**
 * Scale a source PNG into a target pixel box.
 * `contain` letterboxes on transparent; `cover` fills and may crop.
 */
export async function fitPngIntoBox(
  source: MapExportImage,
  boxW: number,
  boxH: number,
  objectFit: ZwMapFigure["objectFit"] = "contain",
): Promise<MapExportImage> {
  const img = await loadImageFromBytes(source.bytes);
  // Allow upscale so contain/cover can fill the print slot (matches preview).
  const scale =
    objectFit === "cover"
      ? Math.max(boxW / source.widthPx, boxH / source.heightPx)
      : Math.min(boxW / source.widthPx, boxH / source.heightPx);
  const drawW = Math.max(1, Math.round(source.widthPx * scale));
  const drawH = Math.max(1, Math.round(source.heightPx * scale));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(boxW));
  canvas.height = Math.max(1, Math.round(boxH));
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("Could not create canvas for map fit");
  // Opaque paper: transparent PNG letterboxing would leave holes in the PDF image.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const x = Math.round((canvas.width - drawW) / 2);
  const y = Math.round((canvas.height - drawH) / 2);
  ctx.drawImage(img, x, y, drawW, drawH);
  const bytes = await canvasToPngBytes(canvas);
  return {
    bytes,
    dataUrl: pngBytesToDataUrl(bytes),
    widthPx: canvas.width,
    heightPx: canvas.height,
  };
}

async function loadMapAssetDataUrls(
  rootPath: string,
  doc: ReturnType<typeof parseMapDocument>,
): Promise<Map<string, string>> {
  const assets = new Map<string, string>();
  try {
    const listed = await listProjectAssets(rootPath);
    await Promise.all(
      listed.map(async (path) => {
        try {
          assets.set(path, await readProjectAssetDataUrl(rootPath, path));
        } catch {
          // Skip missing.
        }
      }),
    );
  } catch {
    // Optional.
  }
  if (typeof doc.landmass !== "undefined") {
    try {
      assets.set(
        doc.landmass.path,
        await readProjectAssetDataUrl(rootPath, doc.landmass.path),
      );
    } catch {
      // Optional.
    }
  }
  return assets;
}

/**
 * Load, render, rotate, and optionally split a manuscript map figure for export.
 */
export async function prepareMapFigureExport(
  figure: ZwMapFigure,
  options: PrepareMapFigureOptions,
): Promise<PreparedMapFigure> {
  const dpi = options.dpi ?? DEFAULT_MAP_EXPORT_DPI;
  const page = printPageSizeFor(options.printSize);
  const content = mapContentSizeInches(page, figure.printExtent);
  const isSpread = figure.spread === "spread";

  const raw = await readProjectFile(
    joinProjectPath(options.rootPath, figure.mapPath),
  );
  const doc = parseMapDocument(raw);
  const assetDataUrls = await loadMapAssetDataUrls(options.rootPath, doc);
  const svg = mapToSvg(doc, { bounds: "page", assetDataUrls });

  // Rasterize at the *map* page aspect (same as preview SVG natural size), not
  // the print page. Stretching into A5 first is what flipped/distorted exports.
  const map = mapPage(doc);
  const workScale = Math.max(dpi / 96, 2);
  const workW = Math.max(1, Math.round(map.width * workScale));
  const workH = Math.max(1, Math.round(map.height * workScale));
  const rawBytes = await rasterizeSvgToPngBytes(svg, workW, workH);
  // Rotate first (matches preview). Single-page PDF then uses CSS object-fit
  // against the live @page content box so crop matches ManuscriptMapPreview.
  const rotated = await rotatePngBytes(rawBytes, figure.orientation);

  if (isSpread === false) {
    return {
      id: figure.id,
      title: figure.title,
      spread: "single",
      printExtent: figure.printExtent,
      objectFit: figure.objectFit,
      full: rotated,
      halves: nothing,
    };
  }

  // Spread: map crosses the spine; only outer left/right keep the trim margin.
  const pageWIn = inchesFromCss(page.width);
  const targetWPx = Math.max(
    1,
    Math.round((pageWIn * 2 - content.marginIn * 2) * dpi),
  );
  const targetHPx = Math.max(1, Math.round(content.height * dpi));
  const fitted = await fitPngIntoBox(
    rotated,
    targetWPx,
    targetHPx,
    figure.objectFit,
  );
  const halves = await splitPngHorizontal(fitted);
  return {
    id: figure.id,
    title: figure.title,
    spread: "spread",
    printExtent: figure.printExtent,
    objectFit: figure.objectFit,
    full: fitted,
    halves: just(halves),
  };
}

/** Fit a prepared single-page map into the print content box (DOCX / tests). */
export async function fitPreparedMapForPrintBox(
  prepared: PreparedMapFigure,
  printSize: ZwPrintSize,
  dpi: number = DEFAULT_MAP_EXPORT_DPI,
): Promise<MapExportImage> {
  if (prepared.spread === "spread") return prepared.full;
  const page = printPageSizeFor(printSize);
  const content = mapContentSizeInches(page, prepared.printExtent);
  return fitPngIntoBox(
    prepared.full,
    Math.max(1, Math.round(content.width * dpi)),
    Math.max(1, Math.round(content.height * dpi)),
    prepared.objectFit,
  );
}

/** EPUB always uses the full image; never verso/recto. */
export function mapImageForEpub(prepared: PreparedMapFigure): MapExportImage {
  return prepared.full;
}

/** PDF/DOCX: spread → halves; single → full. */
export function mapImagesForPrint(
  prepared: PreparedMapFigure,
):
  | { kind: "single"; image: MapExportImage }
  | { kind: "spread"; verso: MapExportImage; recto: MapExportImage } {
  if (prepared.spread === "spread" && isJust(prepared.halves)) {
    return {
      kind: "spread",
      verso: prepared.halves.value.verso,
      recto: prepared.halves.value.recto,
    };
  }
  return { kind: "single", image: prepared.full };
}

export function isPreparedMapSpread(prepared: PreparedMapFigure): boolean {
  return prepared.spread === "spread" && isJust(prepared.halves);
}

/** Pure: whether EPUB should ignore spread (always true by product rule). */
export function epubIgnoresMapSpread(): true {
  return true;
}
